import React, { useState, useRef, useEffect } from 'react';
import { Braces, Maximize2 } from 'lucide-react';
import PromptExpandModal from './PromptExpandModal';
import { TYPE_BADGE_COLORS } from './constants';

export default function ReferenceField({
  label,
  hint,
  value,
  onChange,
  placeholder,
  upstreamNodes = [],
  multiline = false,
  rows = 3,
  expandable = false,
  promptType = 'user',
}) {
  const [open, setOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  // Close on click outside
  useEffect(() => {
    if (!open) return;
    function handleClick(e) {
      if (
        dropdownRef.current && !dropdownRef.current.contains(e.target) &&
        buttonRef.current && !buttonRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const insertReference = (nodeId, path) => {
    const ref = `{{${nodeId}.${path}}}`;
    const el = inputRef.current;
    if (!el) {
      onChange((value || '') + ref);
      setOpen(false);
      return;
    }
    const start = el.selectionStart ?? (value || '').length;
    const end = el.selectionEnd ?? start;
    const before = (value || '').slice(0, start);
    const after = (value || '').slice(end);
    onChange(before + ref + after);
    setOpen(false);
    // Restore cursor after the inserted reference
    requestAnimationFrame(() => {
      const pos = start + ref.length;
      el.focus();
      el.setSelectionRange(pos, pos);
    });
  };

  const inputClasses =
    'w-full px-2.5 py-1.5 text-[13px] border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-gray-300 focus:border-gray-300 placeholder:text-gray-300 transition-colors';

  return (
    <div className="mb-3">
      <div className="flex items-center justify-between mb-1">
        <label className="block text-xs font-medium text-gray-600">{label}</label>
        <div className="flex items-center gap-0.5">
          {/* Expand button */}
          {expandable && (
            <button
              type="button"
              onClick={() => setModalOpen(true)}
              className="p-0.5 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
              title="Expand editor"
            >
              <Maximize2 className="w-3.5 h-3.5" />
            </button>
          )}

          {/* Reference picker */}
          <div className="relative">
            <button
              ref={buttonRef}
              type="button"
              onClick={() => setOpen((prev) => !prev)}
              className={`p-0.5 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors ${open ? 'text-gray-600 bg-gray-100' : ''}`}
              title="Insert node reference"
            >
              <Braces className="w-3.5 h-3.5" />
            </button>

            {open && (
              <div
                ref={dropdownRef}
                className="absolute right-0 top-full mt-1 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-64 overflow-y-auto"
              >
                {upstreamNodes.length === 0 ? (
                  <p className="px-3 py-4 text-xs text-gray-400 italic text-center">
                    No upstream nodes connected
                  </p>
                ) : (
                  upstreamNodes.map((node) => {
                    const Icon = node.icon;
                    return (
                      <div key={node.id}>
                        {/* Node header */}
                        <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-50 border-b border-gray-100">
                          <div
                            className="w-4 h-4 rounded flex items-center justify-center flex-shrink-0"
                            style={{ backgroundColor: `${node.color}20` }}
                          >
                            <Icon className="w-2.5 h-2.5" style={{ color: node.color }} strokeWidth={2.5} />
                          </div>
                          <span className="text-[11px] font-medium text-gray-700 truncate">{node.label}</span>
                        </div>
                        {/* Output paths */}
                        {node.outputSchema.map((output) => (
                          <button
                            key={`${node.id}-${output.path}`}
                            type="button"
                            onClick={() => insertReference(node.id, output.path)}
                            className="w-full text-left px-3 py-1.5 pl-7 flex items-center gap-2 hover:bg-gray-50 transition-colors group"
                          >
                            <span className="text-xs text-gray-700 group-hover:text-gray-900 flex-1 truncate">
                              {output.label}
                            </span>
                            <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full flex-shrink-0 ${TYPE_BADGE_COLORS[output.type] || TYPE_BADGE_COLORS.string}`}>
                              {output.type}
                            </span>
                          </button>
                        ))}
                      </div>
                    );
                  })
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {multiline ? (
        <textarea
          ref={inputRef}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          rows={rows}
          className={`${inputClasses} resize-none`}
        />
      ) : (
        <input
          ref={inputRef}
          type="text"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className={inputClasses}
        />
      )}

      {hint && <p className="text-[11px] text-gray-400 mt-1">{hint}</p>}

      {/* Expand modal */}
      {expandable && (
        <PromptExpandModal
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          label={label}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          upstreamNodes={upstreamNodes}
          promptType={promptType}
        />
      )}
    </div>
  );
}
